import type { StatusVariant } from "@/lib/colors";
import type {
	DeviceConnection,
	NetworkPrinterCandidate,
	NetworkPrinterDriver,
	NetworkPrintersResponse,
	OperationKind,
	OperationStatus,
	OperationStepStatus,
	PrinterDevice,
	PrinterEnv,
	PrinterItem,
	PrinterJobs,
} from "./printers-api";

/**
 * Polskie etykiety modułu drukarek. Operator nie zna Windows, więc każda
 * przyczyna porażki ma komunikat „co się stało” i podpowiedź „co zrobić”.
 * Zbiory `step` i `cause` są zamknięte w kontrakcie (doc/urzadzenia/drukarki.md);
 * nieznana wartość z nowszego backendu dostaje ogólny komunikat, nie pusty ekran.
 */

/** Kroki wykonywane na komputerze z drukarką (A); reszta dzieje się na stanowisku (B). */
export const HOST_SIDE_STEPS: ReadonlySet<string> = new Set([
	"printer",
	"spooler_a",
	"server_a",
	"policy_a",
	"account_a",
	"share_a",
	"firewall_a",
	"unshare_a",
	"firewall_cleanup_a",
]);

export const STEP_LABELS: Record<string, string> = {
	// A: printer_share
	printer: "Drukarka na komputerze z drukarką",
	spooler_a: "Bufor wydruku",
	server_a: "Usługa Serwer",
	policy_a: "Zasady drukowania",
	account_a: "Konto „serwis” do drukowania",
	share_a: "Udostępnienie drukarki",
	firewall_a: "Zapora Windows (sieć lokalna)",
	// B: printer_connect
	reach_b: "Połączenie ze stanowiska do komputera z drukarką",
	session_b: "Zalogowany użytkownik na stanowisku",
	driver_b: "Sterownik drukarki",
	credential_b: "Poświadczenie",
	connect_b: "Drukarka sieciowa na stanowisku",
	default_b: "Drukarka domyślna",
	// B: printer_connect przy naprawie (zmiana adresu przez DHCP)
	replace_b: "Usunięcie starego połączenia",
	// B: printer_disconnect
	disconnect_b: "Usunięcie drukarki sieciowej ze stanowiska",
	restore_default_b: "Przywrócenie poprzedniej drukarki domyślnej",
	// A: printer_unshare
	unshare_a: "Wyłączenie udostępnienia drukarki",
	firewall_cleanup_a: "Usunięcie reguł Zapory Windows",
	// printer_remove — jeden komputer (kolejka albo połączenie)
	remove_session: "Sesja użytkownika Windows",
	remove_default: "Nowa drukarka domyślna",
	remove_printer: "Usunięcie drukarki z komputera",
	remove_connection: "Usunięcie połączenia sieciowego",
	// printer_add — jeden komputer (nowa kolejka sieciowa)
	add_reach: "Połączenie z drukarką",
	add_driver: "Sterownik",
	add_port: "Port TCP/IP",
	add_printer: "Kolejka wydruku",
	add_default: "Drukarka domyślna",
	// printer_fix — jedna kolejka
	fix_purge: "Wyczyszczenie kolejki",
	fix_resume: "Wznowienie drukarki",
	// printer_spooler_reset — cały komputer
	spooler_stop: "Zatrzymanie Bufora wydruku",
	spooler_clean: "Usunięcie zawieszonych zadań",
	spooler_start: "Uruchomienie Bufora wydruku",
	// printer_reinstall — kolejka sieciowa TCP/IP od nowa
	reinstall_check: "Sprawdzenie drukarki i sieci",
	reinstall_driver: "Sterownik",
	reinstall_remove: "Usunięcie starej kolejki",
	reinstall_add: "Założenie kolejki od nowa",
	reinstall_default: "Drukarka domyślna",
	// printer_set_default — sesja jedynego zalogowanego użytkownika
	default_session: "Zalogowany użytkownik Windows",
	default_set: "Ustawienie drukarki domyślnej",
};

/** Kroki `printer_remove`: całość dzieje się na jednym komputerze, bez podziału na A i B. */
export const REMOVE_STEPS: ReadonlySet<string> = new Set([
	"remove_session",
	"remove_default",
	"remove_printer",
	"remove_connection",
]);

/** Kroki `printer_add`: także jeden komputer — ten, na którym powstaje kolejka. */
export const ADD_STEPS: ReadonlySet<string> = new Set([
	"add_reach",
	"add_driver",
	"add_port",
	"add_printer",
	"add_default",
]);

/** Kroki naprawy (`printer_fix`, `printer_spooler_reset`, `printer_reinstall`): jeden komputer. */
export const FIX_STEPS: ReadonlySet<string> = new Set([
	"fix_purge",
	"fix_resume",
	"spooler_stop",
	"spooler_clean",
	"spooler_start",
	"reinstall_check",
	"reinstall_driver",
	"reinstall_remove",
	"reinstall_add",
	"reinstall_default",
]);

/** Kroki `printer_set_default`: jeden komputer, sesja jego zalogowanego użytkownika. */
export const SET_DEFAULT_STEPS: ReadonlySet<string> = new Set(["default_session", "default_set"]);

/**
 * Operacje na jednym komputerze (usuwanie, dodawanie, naprawa, drukarka
 * domyślna) — kroki bez podziału na A i B.
 */
export function singleDeviceOperation(kind: string): boolean {
	return (
		kind === "remove_printer" ||
		kind === "remove_connection" ||
		kind === "add_printer" ||
		kind === "fix_printer" ||
		kind === "reset_spooler" ||
		kind === "reinstall_printer" ||
		kind === "set_default"
	);
}

export function stepLabel(step: string): string {
	return STEP_LABELS[step] ?? step;
}

/** Dopisek przy kroku udanym z `code: kept` (niczego nie zmieniał). */
export function keptLabel(step: string): string {
	switch (step) {
		// `remove_session` z `kept` = sesja nie była potrzebna (usunięcie kolejki bez nowej domyślnej).
		case "remove_session":
			return "(niepotrzebna)";
		case "add_driver":
		case "reinstall_driver":
			return "(już zainstalowany)";
		case "add_port":
			return "(już istniał)";
		case "spooler_stop":
			return "(był już zatrzymany)";
		default:
			return "(bez zmian)";
	}
}

/**
 * Dopisek przy kroku udanym, którego kod mówi, co krok zrobił: `kept` — nic
 * (`keptLabel`), `installed` — sterownik doinstalowany z magazynu sterowników
 * Windows, `created` — `add_port` założył nowy port, `purged` — `fix_purge`
 * usunął zadania, `partial` — `spooler_clean` nie usunął wszystkich plików.
 * `null` = bez dopisku.
 */
export function okStepNote(step: string, code: string | null): string | null {
	if (code === "kept") return keptLabel(step);
	if (code === "installed") return "(zainstalowano)";
	if (code === "created") return "(utworzono)";
	if (code === "purged") return "(wyczyszczono)";
	if (code === "partial") return "(nie wszystko usunięto)";
	return null;
}

/**
 * Kody, które agent zgłasza przy kroku zakończonym POWODZENIEM — to tylko
 * ostrzeżenia. Pokazujemy je dyskretnie w szczegółach technicznych, nie jako błąd.
 */
export const STEP_WARNING_CODES: Record<string, string> = {
	tcp_135_closed_ignored:
		"Port 135 na komputerze z drukarką jest zamknięty — połączenie działa przez port 445.",
	driver_present_unverified:
		"Sterownik był już na stanowisku; nie dało się potwierdzić, że to ta sama wersja co na komputerze z drukarką.",
	connected_by_ip:
		"Połączono po adresie IP — nazwa komputera nie rozwiązuje się w sieci apteki; po zmianie adresu przez DHCP połączenie trzeba będzie naprawić.",
	pending_jobs:
		"W kolejce czekały zadania do wydruku — Windows usunie drukarkę, gdy się wydrukują albo ktoś je anuluje. Do tego czasu może być jeszcze widoczna.",
	partial:
		"Części zawieszonych plików zadań nie dało się usunąć. Jeśli drukowanie dalej stoi, uruchom komputer ponownie.",
};

/** Ostrzeżenia (kody udanych kroków) operacji — po jednym tekście na kod. */
export function operationWarnings(steps: { status: string; code: string | null }[]): string[] {
	const codes = steps
		.filter((s) => s.status !== "failed" && s.code != null && STEP_WARNING_CODES[s.code])
		.map((s) => s.code as string);
	return [...new Set(codes)].map((code) => STEP_WARNING_CODES[code]);
}

export const STEP_STATUS_LABELS: Record<OperationStepStatus, string> = {
	pending: "oczekuje",
	running: "w toku",
	ok: "gotowe",
	failed: "błąd",
	skipped: "pominięte",
};

export const OPERATION_KIND_LABELS: Record<OperationKind, string> = {
	share_connect: "Udostępnienie",
	repair: "Naprawa połączenia",
	disconnect: "Odłączenie",
	unshare: "Cofnięcie udostępnienia",
	forget: "Zapomnienie połączenia",
	remove_printer: "Usunięcie drukarki",
	remove_connection: "Usunięcie połączenia",
	add_printer: "Dodanie drukarki sieciowej",
	fix_printer: "Naprawa drukarki",
	reset_spooler: "Reset bufora wydruku",
	reinstall_printer: "Ponowna instalacja drukarki",
	set_default: "Ustawienie drukarki domyślnej",
};

export function operationKindLabel(kind: string): string {
	return OPERATION_KIND_LABELS[kind as OperationKind] ?? kind;
}

/**
 * Tytuł okna operacji z historii: „Odłączenie drukarki”, ale „Usunięcie
 * drukarki”, „Dodanie drukarki sieciowej” i „Reset bufora wydruku” bez
 * dopisanej „drukarki”.
 */
export function operationTitle(kind: string): string {
	const label = operationKindLabel(kind);
	return /drukark|połączeni|bufor/i.test(label) ? label : `${label} drukarki`;
}

export const OPERATION_STATUS_LABELS: Record<OperationStatus, string> = {
	running: "W toku",
	succeeded: "Udane",
	failed: "Nieudane",
};

export const OPERATION_STATUS_VARIANT: Record<OperationStatus, StatusVariant> = {
	running: "info",
	succeeded: "success",
	failed: "error",
};

export function operationStatusLabel(status: string): string {
	return OPERATION_STATUS_LABELS[status as OperationStatus] ?? status;
}

export type CauseMessage = { title: string; hint: string };

/** Każda przyczyna z tabeli „Przyczyny” kontraktu — test pilnuje kompletności. */
export const CAUSE_MESSAGES: Record<string, CauseMessage> = {
	host_offline: {
		title: "Komputer z drukarką jest wyłączony albo nie ma połączenia z Farmasterem.",
		hint: "Włącz komputer, poczekaj, aż w panelu pojawi się jako online, i spróbuj ponownie.",
	},
	client_offline: {
		title: "Stanowisko jest wyłączone albo nie ma połączenia z Farmasterem.",
		hint: "Włącz stanowisko, poczekaj, aż w panelu pojawi się jako online, i spróbuj ponownie.",
	},
	agent_outdated: {
		title: "Na jednym z komputerów działa za stary agent Farmastera.",
		hint: "Zaktualizuj agenta (Zdalny dostęp → Urządzenia → Aktualizuj agenta) i spróbuj ponownie.",
	},
	device_reassigned: {
		title: "Komputery nie są już przypisane do tej samej apteki.",
		hint: "Sprawdź przypisanie urządzeń w Zdalny dostęp → Urządzenia, odśwież widok i spróbuj ponownie.",
	},
	printer_not_found: {
		title: "Tej drukarki nie ma już na komputerze.",
		hint: "Ktoś mógł ją usunąć albo zmienić jej nazwę. Kliknij „Odśwież” i wybierz drukarkę ponownie.",
	},
	printer_not_local: {
		title: "Ta drukarka nie jest podłączona kablem do komputera.",
		hint: "Udostępnić można tylko drukarkę na USB, LPT albo COM. Drukarkę sieciową dodaj na stanowisku bezpośrednio.",
	},
	spooler_not_running: {
		title: "Usługa „Bufor wydruku” nie działa.",
		hint: "Kliknij „Zresetuj bufor wydruku” na karcie komputera wskazanego w szczegółach albo uruchom ten komputer ponownie.",
	},
	server_service_not_running: {
		title: "Na komputerze z drukarką nie działa usługa „Serwer”.",
		hint: "Bez niej Windows nie udostępnia drukarek. Włącz usługę Serwer (LanmanServer) z uruchamianiem automatycznym.",
	},
	spooler_remote_disabled: {
		title: "Zasady Windows na komputerze z drukarką blokują udostępnianie drukarek.",
		hint: "Zasada „Zezwalaj buforowi wydruku na akceptowanie połączeń klientów” jest wyłączona — zmienić ją może administrator komputera.",
	},
	account_failed: {
		title: "Nie udało się przygotować konta „serwis” na komputerze z drukarką.",
		hint: "Komputer może mieć zasady blokujące konta lokalne. Spróbuj ponownie, a gdy błąd wróci, przekaż szczegóły techniczne do IT.",
	},
	service_account_blocked: {
		title:
			"Konto „serwis” na komputerze z drukarką jest wyłączone, zablokowane albo ma wygasłe hasło.",
		hint: "Farmaster nie zmienia istniejącego konta „serwis”, bo korzysta z niego także udział KS. Niech administrator komputera je odblokuje, potem ponów udostępnienie.",
	},
	service_account_password_mismatch: {
		title:
			"Hasło konta „serwis” na komputerze z drukarką jest inne niż hasło zapisane w Farmasterze.",
		hint: "Farmaster nie zmienia hasła istniejącego konta. Ustaw na tym komputerze hasło z Zdalny dostęp → Urządzenia → „Hasła serwisowe” albo popraw je tam, potem ponów.",
	},
	service_account_network_denied: {
		title:
			"Zasady Windows na komputerze z drukarką nie pozwalają kontu „serwis” logować się przez sieć.",
		hint: "Konto (albo jego grupa) jest w zasadzie „Odmowa dostępu do tego komputera z sieci” — zmienić ją może administrator komputera.",
	},
	service_password_not_set: {
		title: "Hasło konta serwisowego „serwis” nie jest ustawione w Farmasterze.",
		hint: "Wpisz je w Zdalny dostęp → Urządzenia → „Hasła serwisowe” i spróbuj ponownie.",
	},
	service_password_invalid: {
		title:
			"Hasło konta serwisowego „serwis” w Farmasterze ma niedozwolony znak albo jest za długie.",
		hint: "Windows przyjmuje najwyżej 127 znaków. Popraw je w Zdalny dostęp → Urządzenia → „Hasła serwisowe” i spróbuj ponownie.",
	},
	service_password_changed: {
		title: "Hasło konta serwisowego „serwis” zmieniło się w trakcie udostępniania.",
		hint: "Komputer z drukarką sprawdził poprzednie hasło, więc stanowisko nie dostało nowego. Udostępnij drukarkę ponownie.",
	},
	share_failed: {
		title: "Windows nie pozwolił udostępnić drukarki.",
		hint: "Spróbuj ponownie. Gdy błąd wróci, przekaż szczegóły techniczne do IT.",
	},
	share_name_unsupported: {
		title: "Nazwy drukarki nie da się użyć jako nazwy udostępnienia.",
		hint: "Zmień nazwę drukarki w Windows na prostszą (litery, cyfry, myślniki) i spróbuj ponownie.",
	},
	firewall_rule_failed: {
		title: "Nie udało się dodać reguły Zapory Windows dla drukowania.",
		hint: "Zaporą może zarządzać inny program albo zasady domeny. Przekaż szczegóły techniczne do IT.",
	},
	network_public: {
		title: "Sieć komputera z drukarką jest oznaczona jako publiczna.",
		hint: "Oznacz sieć jako prywatną w ustawieniach Windows albo zgłoś to do IT.",
	},
	blocked_firewall_windows: {
		title: "Zapora Windows na komputerze z drukarką blokuje połączenie ze stanowiska.",
		hint: "Reguła Farmastera nie działa albo jest wyłączona. Sprawdź Zaporę Windows na komputerze z drukarką i udostępnij drukarkę ponownie.",
	},
	blocked_eset: {
		title: "ESET na komputerze z drukarką blokuje połączenie ze stanowiska.",
		hint: "Zapora ESET pomija reguły Windows. Dodaj w ESET wyjątek dla udostępniania plików i drukarek w sieci lokalnej (porty 445 i 135) albo oznacz sieć jako zaufaną.",
	},
	unreachable: {
		title: "Stanowisko nie może połączyć się z komputerem z drukarką.",
		hint: "Sprawdź, czy oba komputery są w tej samej sieci lokalnej i czy nie blokuje ich inna zapora lub router.",
	},
	no_user_session: {
		title: "Na stanowisku nikt nie jest zalogowany.",
		hint: "Drukarkę sieciową dodaje się zalogowanemu użytkownikowi. Zaloguj się na stanowisku i spróbuj ponownie.",
	},
	multiple_users: {
		title: "Na stanowisku jest zalogowanych kilku użytkowników Windows.",
		hint: "Drukarka sieciowa należy do jednego konta. Wyloguj pozostałych użytkowników (także rozłączone sesje) i spróbuj ponownie.",
	},
	wrong_user: {
		title: "Na stanowisku jest zalogowany inny użytkownik Windows niż właściciel tej drukarki.",
		hint: "Drukarka sieciowa należy do konta Windows, na którym ją dodano. Zaloguj to konto na stanowisku i ponów operację (także gdy użytkownik zmienił się w trakcie udostępniania).",
	},
	credential_failed: {
		title: "Nie udało się zapisać poświadczenia na stanowisku.",
		hint: "Spróbuj ponownie. Gdy błąd wróci, przekaż szczegóły techniczne do IT.",
	},
	credential_foreign: {
		title:
			"Stanowisko ma już zapisane poświadczenie do komputera z drukarką na inne konto niż „serwis”.",
		hint: "Farmaster go nie nadpisuje: Windows pozwala na jedno konto na komputer, więc zmiana odcięłaby inny udział. Usuń albo zmień ten wpis w Menedżerze poświadczeń użytkownika na stanowisku i ponów.",
	},
	auth_failed: {
		title: "Konto „serwis” na komputerze z drukarką odrzuciło logowanie stanowiska.",
		hint: "Sprawdź, czy hasło konta „serwis” na tym komputerze zgadza się z hasłem w „Hasłach serwisowych”, i ponów. Uwaga: kilka nieudanych prób blokuje konto na 10 minut.",
	},
	driver_failed: {
		title: "Stanowisko nie pobrało sterownika z komputera z drukarką.",
		hint: "Zainstaluj sterownik tej drukarki na stanowisku (zdalnie, jako administrator) i ponów.",
	},
	rpc_failed: {
		title: "Błąd komunikacji z Buforem wydruku (RPC).",
		hint: "Uruchom ponownie oba komputery (albo usługę Bufor wydruku na nich) i spróbuj jeszcze raz.",
	},
	connect_failed: {
		title: "Nie udało się dodać drukarki sieciowej na stanowisku.",
		hint: "Spróbuj ponownie. Gdy błąd wróci, przekaż szczegóły techniczne do IT.",
	},
	default_failed: {
		title: "Drukarka jest dodana, ale nie udało się ustawić jej jako domyślnej.",
		hint: "Ustaw ją jako domyślną ręcznie w Ustawieniach Windows na stanowisku.",
	},
	printer_not_removable: {
		title: "Tej drukarki nie można usunąć zdalnie.",
		hint: "Drukarkę z pulpitu zdalnego odtwarza sama sesja zdalna, a Microsoft Print to PDF jest częścią Windows — zostaw je. Kliknij „Odśwież”, jeśli lista drukarek jest nieaktualna.",
	},
	remove_failed: {
		title: "Windows nie pozwolił usunąć drukarki ani połączenia.",
		hint: "Drukarka mogła być akurat w użyciu. Spróbuj ponownie za chwilę; gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	// Dodawanie drukarki sieciowej (printer_add)
	printer_unreachable: {
		title: "Komputer nie łączy się z drukarką.",
		hint: "Sprawdź, czy drukarka jest włączona i w tej samej sieci co komputer, potem spróbuj ponownie.",
	},
	network_changed: {
		title: "Komputer jest już w innej sieci niż drukarka.",
		hint: "Komputer zmienił sieć (np. laptop poza biurem). Dodaj drukarkę, gdy wróci do sieci, w której ona stoi.",
	},
	driver_not_available: {
		title: "Sterownika nie ma na komputerze.",
		hint: "Wybierz inny sterownik (np. ogólny) albo zainstaluj sterownik producenta na komputerze i spróbuj ponownie.",
	},
	port_failed: {
		title: "Windows nie założył portu TCP/IP dla drukarki.",
		hint: "Spróbuj ponownie. Gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	printer_exists: {
		title: "Na komputerze jest już drukarka o tej nazwie.",
		hint: "Dodaj drukarkę ponownie pod inną nazwą. Kliknij „Odśwież”, jeśli lista drukarek jest nieaktualna.",
	},
	add_failed: {
		title: "Windows nie pozwolił dodać drukarki.",
		hint: "Spróbuj ponownie albo wybierz inny sterownik. Gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	// Naprawa drukarki (printer_fix, printer_spooler_reset, printer_reinstall)
	printer_not_fixable: {
		title: "Tej drukarki nie naprawiamy zdalnie.",
		hint: "To drukarka przekierowana z pulpitu zdalnego — należy do sesji zdalnej. Rozłącz sesję zdalną i połącz się ponownie.",
	},
	purge_failed: {
		title: "Windows nie pozwolił wyczyścić kolejki drukarki.",
		hint: "Spróbuj „Zresetuj bufor wydruku” na karcie komputera albo uruchom komputer ponownie. Gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	resume_failed: {
		title: "Windows nie pozwolił wznowić drukarki.",
		hint: "Sprawdź, czy drukarka jest włączona i podłączona, i spróbuj ponownie; pomóc może też „Zresetuj bufor wydruku”. Gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	spooler_disabled: {
		title:
			"Bufor wydruku jest wyłączony w Windows (typ startu: wyłączony) — to bywa świadome zabezpieczenie; nie włączamy go z panelu.",
		hint: "Niczego nie zmieniono. Jeśli ten komputer ma drukować, poproś IT o włączenie usługi Bufor wydruku.",
	},
	stop_failed: {
		title: "Nie udało się zatrzymać usługi Bufor wydruku.",
		hint: "Usługa mogła się zawiesić. Uruchom komputer ponownie; gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	clean_failed: {
		title: "Nie udało się usunąć zawieszonych zadań z bufora wydruku.",
		hint: "Usługę próbowaliśmy uruchomić z powrotem — sprawdź w krokach, czy się udało. Jeśli drukowanie dalej stoi, uruchom komputer ponownie.",
	},
	start_failed: {
		title:
			"Bufor wydruku został zatrzymany, ale nie udało się go uruchomić ponownie — na tym komputerze nie działa teraz drukowanie.",
		hint: "Uruchom komputer ponownie. Gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	printer_not_reinstallable: {
		title: "Tej drukarki nie da się zainstalować ponownie zdalnie.",
		hint: "Ponownie instalujemy tylko drukarki sieciowe z portem TCP/IP — kolejkę USB albo WSD Windows zakłada sam po podłączeniu drukarki. Kliknij „Odśwież”, jeśli lista drukarek jest nieaktualna.",
	},
	timeout: {
		title: "Komputer nie odpowiedział na czas.",
		hint: "Sprawdź, czy oba komputery są włączone i online, potem spróbuj ponownie.",
	},
	agent_error: {
		title: "Agent Farmastera zgłosił błąd podczas wykonywania polecenia.",
		hint: "Spróbuj ponownie. Gdy błąd wróci, przekaż szczegóły techniczne do IT.",
	},
};

/**
 * Przy usuwaniu część przyczyn znaczy co innego niż przy udostępnianiu: działa
 * jeden komputer, a nową domyślną agent ustawia PRZED usunięciem — backend daje
 * `default_failed` także za `new_default_missing`, czyli nic nie zostało usunięte.
 * `device_reassigned` dotyczy jednego komputera, także w grupie urządzeń bez apteki.
 */
const REMOVE_CAUSE_MESSAGES: Record<string, CauseMessage> = {
	device_reassigned: {
		title: "Komputer w międzyczasie przypisano do innej apteki albo zmieniono jego rolę.",
		hint: "Odśwież widok i znajdź komputer w jego obecnym miejscu na liście; przypisanie sprawdzisz w Zdalny dostęp → Urządzenia.",
	},
	default_failed: {
		title: "Nie udało się ustawić nowej drukarki domyślnej, więc niczego nie usunięto.",
		hint: "Wybranej drukarki mogło już nie być u użytkownika. Kliknij „Odśwież” i usuń ponownie, wybierając inną domyślną albo „Windows wybierze sam”.",
	},
	no_user_session: {
		title: "Na komputerze nikt nie jest zalogowany.",
		hint: "Połączenie sieciowe i drukarka domyślna należą do konta Windows. Zaloguj jego właściciela na tym komputerze i ponów usunięcie.",
	},
	wrong_user: {
		title:
			"Na komputerze jest zalogowany inny użytkownik Windows niż właściciel połączenia albo drukarki domyślnej.",
		hint: "Zaloguj właściwe konto Windows na tym komputerze i ponów usunięcie.",
	},
	timeout: {
		title: "Komputer nie odpowiedział na czas.",
		hint: "Sprawdź, czy komputer jest włączony i online, potem kliknij „Odśwież” — drukarka mogła zostać usunięta mimo braku odpowiedzi.",
	},
};

/**
 * Przy dodawaniu działa jeden komputer, a domyślną agent ustawia NA KOŃCU
 * (`add_default`) — porażka sesji albo domyślnej znaczy, że drukarka już jest.
 */
const ADD_CAUSE_MESSAGES: Record<string, CauseMessage> = {
	host_offline: {
		title: "Komputer jest wyłączony albo nie ma połączenia z Farmasterem.",
		hint: "Włącz komputer, poczekaj, aż w panelu pojawi się jako online, i spróbuj ponownie.",
	},
	agent_outdated: {
		title: "Na komputerze działa za stary agent Farmastera.",
		hint: "Zaktualizuj agenta (Zdalny dostęp → Urządzenia → Aktualizuj agenta) i spróbuj ponownie.",
	},
	device_reassigned: REMOVE_CAUSE_MESSAGES.device_reassigned,
	driver_failed: {
		title: "Windows nie zainstalował sterownika drukarki.",
		hint: "Wybierz inny sterownik (np. ogólny) albo zainstaluj sterownik producenta na komputerze i spróbuj ponownie.",
	},
	no_user_session: {
		title:
			"Drukarka jest dodana, ale nie została domyślną — na komputerze nikt nie jest zalogowany.",
		hint: "Drukarkę domyślną ustawia się zalogowanemu użytkownikowi. Ustaw ją ręcznie w Ustawieniach Windows po zalogowaniu.",
	},
	multiple_users: {
		title:
			"Drukarka jest dodana, ale nie została domyślną — na komputerze jest zalogowanych kilku użytkowników Windows.",
		hint: "Nie wiadomo, któremu z nich ją ustawić. Ustaw ją jako domyślną ręcznie w Ustawieniach Windows u właściwego użytkownika.",
	},
	default_failed: {
		title: "Drukarka jest dodana, ale nie udało się ustawić jej jako domyślnej.",
		hint: "Ustaw ją jako domyślną ręcznie w Ustawieniach Windows na tym komputerze.",
	},
	timeout: {
		title: "Komputer nie odpowiedział na czas.",
		hint: "Sprawdź, czy komputer jest włączony i online, potem kliknij „Odśwież” — drukarka mogła zostać dodana mimo braku odpowiedzi.",
	},
};

/** Naprawa kolejki i reset bufora: jeden komputer, jak przy dodawaniu. */
const FIX_CAUSE_MESSAGES: Record<string, CauseMessage> = {
	host_offline: ADD_CAUSE_MESSAGES.host_offline,
	agent_outdated: ADD_CAUSE_MESSAGES.agent_outdated,
	device_reassigned: REMOVE_CAUSE_MESSAGES.device_reassigned,
	timeout: {
		title: "Komputer nie odpowiedział na czas.",
		hint: "Sprawdź, czy komputer jest włączony i online, potem kliknij „Odśwież” — naprawa mogła się wykonać mimo braku odpowiedzi.",
	},
};

/**
 * Ponowna instalacja: sterownik i sieć agent sprawdza PRZED usunięciem kolejki
 * (porażka = nic nie zmieniono), domyślną ustawia NA KOŃCU (porażka = drukarka
 * już jest), a porażka `reinstall_add` znaczy, że stara kolejka już zniknęła.
 */
const REINSTALL_CAUSE_MESSAGES: Record<string, CauseMessage> = {
	...FIX_CAUSE_MESSAGES,
	network_changed: {
		title: "Komputer jest już w innej sieci niż drukarka, więc niczego nie zmieniono.",
		hint: "Zainstaluj drukarkę ponownie, gdy komputer wróci do sieci, w której ona stoi.",
	},
	driver_not_available: {
		title: "Wybranego sterownika nie ma na komputerze, więc kolejki nie ruszaliśmy.",
		hint: "Wybierz inny sterownik (np. bieżący albo ogólny) i spróbuj ponownie.",
	},
	driver_failed: {
		title: "Windows nie zainstalował sterownika, więc kolejki nie ruszaliśmy.",
		hint: ADD_CAUSE_MESSAGES.driver_failed.hint,
	},
	remove_failed: {
		title: "Windows nie pozwolił usunąć starej kolejki, więc drukarka została na komputerze.",
		hint: "Drukarka mogła być akurat w użyciu. Spróbuj ponownie za chwilę; gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	add_failed: {
		title:
			"Stara kolejka została usunięta, ale Windows nie pozwolił założyć jej od nowa — tej drukarki nie ma teraz na komputerze.",
		hint: "Dodaj ją przyciskiem „Dodaj drukarkę sieciową” na karcie komputera (ten sam adres, możesz wybrać inny sterownik). Gdy błąd wróci, przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	no_user_session: {
		title:
			"Drukarka jest zainstalowana ponownie, ale nie wróciła jako domyślna — na komputerze nikt nie jest zalogowany.",
		hint: "Ustaw ją jako domyślną ręcznie w Ustawieniach Windows po zalogowaniu.",
	},
	multiple_users: {
		title:
			"Drukarka jest zainstalowana ponownie, ale nie wróciła jako domyślna — na komputerze jest zalogowanych kilku użytkowników Windows.",
		hint: "Ustaw ją jako domyślną ręcznie w Ustawieniach Windows u właściwego użytkownika.",
	},
	default_failed: {
		title: "Drukarka jest zainstalowana ponownie, ale nie udało się przywrócić jej jako domyślnej.",
		hint: "Ustaw ją jako domyślną ręcznie w Ustawieniach Windows na tym komputerze.",
	},
	timeout: {
		title: "Komputer nie odpowiedział na czas.",
		hint: "Sprawdź, czy komputer jest online, i kliknij „Odśwież”. Jeśli tej drukarki nie ma na liście, dodaj ją przyciskiem „Dodaj drukarkę sieciową”.",
	},
};

/**
 * Drukarka domyślna to ustawienie jedynego zalogowanego użytkownika Windows —
 * przyczyny mówią o nim, nie o stanowisku ani o udostępnianiu.
 */
const SET_DEFAULT_CAUSE_MESSAGES: Record<string, CauseMessage> = {
	...FIX_CAUSE_MESSAGES,
	no_user_session: {
		title: "Na komputerze nikt nie jest zalogowany w Windows.",
		hint: "Drukarka domyślna to ustawienie użytkownika Windows. Zaloguj się na komputerze i spróbuj ponownie.",
	},
	multiple_users: {
		title: "Na komputerze jest zalogowanych kilku użytkowników Windows.",
		hint: "Nie wiadomo, któremu ustawić drukarkę domyślną. Wyloguj pozostałych (także rozłączone sesje) i spróbuj ponownie.",
	},
	wrong_user: {
		title: "Na komputerze zalogował się inny użytkownik Windows niż w danych panelu.",
		hint: "Kliknij „Odśwież”, poczekaj kilka sekund na nową listę drukarek i spróbuj ponownie.",
	},
	printer_not_found: {
		title: "Tej drukarki nie ma u zalogowanego użytkownika Windows.",
		hint: "Połączenie sieciowe należy do konta, na którym je dodano — wybierz inną drukarkę albo zaloguj tamto konto. Kliknij „Odśwież”, jeśli lista jest nieaktualna.",
	},
	default_failed: {
		title: "Windows nie pozwolił ustawić drukarki domyślnej.",
		hint: "Spróbuj ponownie. Gdy błąd wróci, ustaw ją ręcznie w Ustawieniach Windows i przekaż szczegóły techniczne (kod Windows) do IT.",
	},
	timeout: {
		title: "Komputer nie odpowiedział na czas.",
		hint: "Sprawdź, czy komputer jest włączony i online, potem kliknij „Odśwież” — drukarka mogła zostać ustawiona mimo braku odpowiedzi.",
	},
};

function causeMessage(cause: string, kind: string | undefined): CauseMessage | undefined {
	let special: Record<string, CauseMessage> | undefined;
	if (kind === "remove_printer" || kind === "remove_connection") special = REMOVE_CAUSE_MESSAGES;
	else if (kind === "add_printer") special = ADD_CAUSE_MESSAGES;
	else if (kind === "fix_printer" || kind === "reset_spooler") special = FIX_CAUSE_MESSAGES;
	else if (kind === "reinstall_printer") special = REINSTALL_CAUSE_MESSAGES;
	else if (kind === "set_default") special = SET_DEFAULT_CAUSE_MESSAGES;
	return special?.[cause] ?? CAUSE_MESSAGES[cause];
}

/**
 * Komunikat dla `cause` (z uwzględnieniem rodzaju operacji); nieznana przyczyna
 * dostaje ogólny tekst z kodem i `errorDetail`.
 */
export function describeCause(
	cause: string | null,
	errorDetail: string | null,
	kind?: string,
): CauseMessage {
	const known = cause ? causeMessage(cause, kind) : undefined;
	if (known) return known;
	return {
		title: cause
			? `Operacja nie powiodła się (przyczyna: ${cause}).`
			: "Operacja nie powiodła się.",
		hint: errorDetail ?? "Spróbuj ponownie. Gdy błąd wróci, przekaż szczegóły techniczne do IT.",
	};
}

/** Krótka etykieta przyczyny do tabeli historii (pierwsze zdanie komunikatu). */
export function causeShortLabel(cause: string | null, kind?: string): string {
	if (!cause) return "";
	const known = causeMessage(cause, kind);
	return known ? known.title.replace(/\.$/, "") : cause;
}

/** Kody 409 z backendu (`AppError::ConflictWithCode`) dla startu operacji. */
export const CONFLICT_MESSAGES: Record<string, string> = {
	device_offline: "Urządzenie jest offline — włącz je i spróbuj ponownie.",
	agent_outdated: "Urządzenie ma za starego agenta Farmastera — najpierw go zaktualizuj.",
	not_same_pharmacy: "Komputery nie są przypisane do tej samej apteki.",
	operation_running: "Trwa już inna operacja na drukarkach — poczekaj na jej koniec.",
	share_in_use: "Z drukarki korzystają jeszcze stanowiska — najpierw je odłącz.",
	printer_not_cable: "Udostępnić można tylko drukarkę podłączoną kablem (USB, LPT, COM).",
	device_online: "Stanowisko jest online — użyj „Odłącz”, żeby usunąć drukarkę także z komputera.",
	repair_limit:
		"Po kilku nieudanych naprawach połączenie trzeba odłączyć i udostępnić drukarkę ponownie.",
	// Usuwanie drukarek i połączeń
	printer_not_found: "Tej drukarki nie ma już na komputerze — kliknij „Odśwież”.",
	printer_not_removable:
		"Tej drukarki nie można usunąć zdalnie (drukarka z pulpitu zdalnego albo Microsoft Print to PDF).",
	share_active: "Drukarka jest udostępniona przez Farmaster — najpierw cofnij udostępnienie.",
	printer_in_use:
		"Na tej drukarce drukują inne komputery — najpierw je odłącz albo usuń ich połączenia.",
	connection_not_found: "Tego połączenia nie ma już na komputerze — kliknij „Odśwież”.",
	connection_managed: "To połączenie założył Farmaster — użyj „Odłącz”.",
	connection_path_unsupported:
		"Ścieżki tego połączenia nie obsługujemy zdalnie — usuń je ręcznie na komputerze.",
	connection_owner_ambiguous:
		"Na komputerze jest kilku użytkowników Windows o tej nazwie, więc nie wiadomo, czyje to połączenie — usuń je ręcznie.",
	new_default_invalid:
		"Wybrana nowa drukarka domyślna nie pasuje do bieżącego stanu komputera — odśwież widok i wybierz ponownie.",
	default_owner_ambiguous:
		"Ta drukarka jest domyślna u kilku użytkowników Windows, więc nie wiadomo, komu ustawić nową — wybierz „Windows wybierze sam”.",
	// Dodawanie drukarki sieciowej
	no_network:
		"Komputer nie zgłosił sieci z bramą domyślną — kliknij „Odśwież”, a gdy to nie pomoże, sprawdź jego połączenie sieciowe.",
	host_outside_network: "Ten adres nie należy do sieci komputera — wpisz adres z jego podsieci.",
	printer_not_available:
		"Tej drukarki nie ma już na liście drukarek w sieci komputera — kliknij „Wykryj ponownie”.",
	printer_installed: "Ta drukarka jest już zainstalowana na komputerze.",
	driver_not_allowed:
		"Tego sterownika nie można użyć dla tej drukarki — wybierz sterownik z listy.",
	printer_exists: "Na komputerze jest już drukarka o tej nazwie — wpisz inną nazwę.",
	no_inventory:
		"Komputer nie przysłał jeszcze czytelnych danych drukarek — kliknij „Odśwież” i spróbuj ponownie.",
	discovery_running:
		"Wykrywanie w tej sieci już trwa — sprawdź ten adres, gdy się skończy (najwyżej pół minuty).",
	network_changed:
		"Komputer jest już w innej sieci niż w ostatnich danych — dane odświeżą się same za chwilę, potem kliknij „Wykryj ponownie”.",
	// Naprawa drukarki i drukarka domyślna
	printer_not_fixable:
		"Drukarka przekierowana z pulpitu zdalnego należy do sesji zdalnej — panel jej nie zmienia.",
	printer_not_reinstallable:
		"Ponownie zainstalować można tylko drukarkę sieciową TCP/IP — kliknij „Odśwież”, jeśli lista drukarek jest nieaktualna.",
	no_user_session:
		"Na komputerze nikt nie jest zalogowany w Windows, a drukarka domyślna to ustawienie użytkownika — zaloguj się i spróbuj ponownie.",
	multiple_users:
		"Na komputerze jest zalogowanych kilku użytkowników Windows, więc nie wiadomo, komu ustawić domyślną — wyloguj pozostałych i spróbuj ponownie.",
	already_default: "Ta drukarka już jest domyślna — widok odświeży się za chwilę.",
	connection_other_user:
		"To połączenie ma inny użytkownik Windows niż zalogowany teraz na komputerze — ustawić je jako domyślne można tylko w jego sesji.",
	// Konto `serwis` (udostępnianie i naprawa połączenia)
	service_password_not_set:
		"Hasło konta serwisowego „serwis” nie jest ustawione — wpisz je w Zdalny dostęp → Urządzenia → „Hasła serwisowe”.",
	service_password_invalid:
		"Hasło konta serwisowego „serwis” ma niedozwolony znak albo ponad 127 znaków — popraw je w Zdalny dostęp → Urządzenia → „Hasła serwisowe”.",
};

export const PORT_KIND_LABELS: Record<string, string> = {
	usb: "USB",
	dot4: "USB",
	lpt: "LPT",
	com: "COM",
	network: "Sieć",
	wsd: "WSD",
	redirected: "Pulpit zdalny",
	virtual: "Wirtualna",
	other: "Inny port",
};

export function portKindLabel(portKind: string): string {
	return PORT_KIND_LABELS[portKind] ?? portKind;
}

/** Forma po liczebniku: 1 → `one`, 2–4 (22–24…) → `few`, reszta → `many`. */
export function pluralPl(count: number, one: string, few: string, many: string): string {
	if (count === 1) return one;
	const mod10 = count % 10;
	const mod100 = count % 100;
	return mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many;
}

/** Odmiana „drukarka” po liczebniku: 1 drukarka, 2–4 (22–24…) drukarki, reszta drukarek. */
function printersNoun(count: number): string {
	return pluralPl(count, "drukarka", "drukarki", "drukarek");
}

/** „1 prawdopodobnie zbędna”, „3 prawdopodobnie zbędne”, „5 prawdopodobnie zbędnych”. */
export function redundantCountLabel(count: number): string {
	return `${count} prawdopodobnie ${pluralPl(count, "zbędna", "zbędne", "zbędnych")}`;
}

export type CableHostSummary = { label: string; ports: string; description: string };

/**
 * Znacznik komputera, który może udostępnić drukarkę: liczba drukarek
 * podłączonych kablem teraz i rodzaje portów. Rola komputera nie ma znaczenia —
 * udostępnia stanowisko tak samo jak serwer. `null` = brak drukarki na kablu.
 */
export function cableHostSummary(printers: PrinterItem[]): CableHostSummary | null {
	const cable = printers.filter((p) => p.cable);
	if (cable.length === 0) return null;
	const ports = [...new Set(cable.map((p) => portKindLabel(p.portKind)))].join(", ");
	const one = cable.length === 1;
	return {
		label: one ? "Drukarka na kablu" : `${cable.length} ${printersNoun(cable.length)} na kablu`,
		ports,
		description: `${one ? "Drukarka podłączona" : "Drukarki podłączone"} kablem (${ports}) do tego komputera. Można ${one ? "ją" : "je"} udostępnić innym stanowiskom tej apteki przyciskiem „Udostępnij na…”.`,
	};
}

/**
 * Role, którym backend zakłada reguły zapory dla wszystkich profili sieci
 * (`firewall_profiles_for_role`): stałe komputery w aptece. Na nich sieć
 * publiczna nie blokuje udostępniania; pozostałe role (laptopy, IT,
 * `franchise_other`) dostają reguły tylko w sieci prywatnej i domenowej.
 */
const ALL_PROFILE_ROLES: ReadonlySet<string> = new Set([
	"pharmacy_workstation",
	"server",
	"franchise",
	"franchise_managed",
	"natubay",
]);

export type EnvWarning = {
	id: string;
	label: string;
	description: string;
	tone: StatusVariant;
};

const SERVICE_STATE: Record<string, string> = {
	stopped: "zatrzymana",
	disabled: "wyłączona",
	missing: "brak usługi",
};

/**
 * Ostrzeżenia ze środowiska Windows. Bufor wydruku dotyczy każdego komputera;
 * usługa Serwer, sieć publiczna, zasada spoolera i zapora ESET blokują tylko
 * UDOSTĘPNIANIE, więc pokazujemy je wyłącznie na komputerze z drukarką na
 * kablu — na zwykłym stanowisku byłyby szumem. Sieć publiczna blokuje tylko
 * role bez reguł zapory dla wszystkich profili (`ALL_PROFILE_ROLES`).
 */
export function envWarnings(
	env: PrinterEnv | null,
	canHost: boolean,
	deviceRole: string,
): EnvWarning[] {
	if (!env) return [];
	const warnings: EnvWarning[] = [];
	const spooler = SERVICE_STATE[env.spooler];
	if (spooler) {
		warnings.push({
			id: "spooler",
			label: `Bufor wydruku: ${spooler}`,
			description:
				"Usługa Bufor wydruku nie działa — na tym komputerze nie zadziała drukowanie ani udostępnianie drukarek.",
			tone: "error",
		});
	}
	if (!canHost) return warnings;
	const server = SERVICE_STATE[env.lanmanServer];
	if (server) {
		warnings.push({
			id: "server",
			label: `Usługa Serwer: ${server}`,
			description:
				"Bez usługi Serwer (LanmanServer) ten komputer nie może udostępniać drukarek innym stanowiskom.",
			tone: "warning",
		});
	}
	if (env.networkCategory === "public" && !ALL_PROFILE_ROLES.has(deviceRole)) {
		warnings.push({
			id: "network",
			label: "Sieć publiczna",
			description:
				"Windows traktuje sieć jako publiczną i blokuje udostępnianie. Oznacz sieć jako prywatną w ustawieniach Windows albo zgłoś to do IT.",
			tone: "warning",
		});
	}
	if (SERVICE_STATE[env.firewallService]) {
		warnings.push({
			id: "firewall-service",
			label: "Zapora Windows (usługa) nie działa",
			description:
				"Usługa Zapory Windows jest zatrzymana albo wyłączona — Farmaster nie doda reguły pozwalającej stanowiskom drukować na tym komputerze.",
			tone: "warning",
		});
	}
	if (env.spoolerRemoteRpc === "disabled") {
		warnings.push({
			id: "policy",
			label: "Zasady blokują udostępnianie",
			description:
				"Zasada „Zezwalaj buforowi wydruku na akceptowanie połączeń klientów” jest wyłączona — stanowiska nie połączą się z tą drukarką.",
			tone: "warning",
		});
	}
	if (env.eset?.firewall) {
		warnings.push({
			id: "eset",
			label: "ESET z zaporą",
			description: `${env.eset.product}${env.eset.version ? ` ${env.eset.version}` : ""} ma własną zaporę, która może blokować stanowiska. Jeśli połączenie się nie uda, dodaj w ESET wyjątek dla drukowania w sieci lokalnej.`,
			tone: "info",
		});
	}
	return warnings;
}

/** Czy urządzenie może przyjąć komendę drukarek teraz (online + nowy agent z inwentarzem). */
export function deviceActionable(device: PrinterDevice): boolean {
	return device.online && device.supported;
}

export const ADDRESS_STALE_LABEL = "Adres komputera z drukarką się zmienił — drukowanie nie działa";

export const BY_IP_EXPLANATION =
	"Stanowisko łączy się z drukarką po adresie IP, bo nazwa komputera z drukarką nie rozwiązuje się w sieci apteki. Gdy router (DHCP) nada temu komputerowi inny adres, drukowanie przestanie działać i połączenie trzeba będzie naprawić. „Napraw połączenie” przepina je na nazwę komputera, jeśli ta już się rozwiązuje.";

/**
 * Powód, dla którego naprawa połączenia nie może teraz ruszyć; `null` = może.
 * Naprawa wysyła polecenie do stanowiska (B) z bieżącym adresem komputera z
 * drukarką (A) z jego inwentarza, więc oba muszą być online i obsługiwane.
 */
export function repairBlocker(
	client: PrinterDevice,
	host: PrinterDevice | undefined,
	locked: boolean,
): string | null {
	if (locked) return "Trwa inna operacja w tej aptece — naprawa będzie możliwa po jej zakończeniu.";
	if (!client.online) return "Naprawa wymaga, żeby to stanowisko było online.";
	if (!client.supported) return "Naprawa wymaga nowszego agenta na tym stanowisku.";
	if (!host) return "Komputera z drukarką nie ma już w tej aptece.";
	if (!host.online)
		return `Naprawa wymaga, żeby komputer z drukarką (${host.displayName}) był online.`;
	if (!host.supported) {
		return `Naprawa wymaga nowszego agenta na komputerze z drukarką (${host.displayName}).`;
	}
	return null;
}

/** Sufiks, który Windows dopisuje kolejce założonej ponownie: „(Kopia 1)”, „(Copy 2)”. */
const COPY_SUFFIX = /\s*\((kopia|copy) \d+\)$/i;

/**
 * Kolejki, których nie grupujemy jako duplikatów: wirtualne, z pulpitu zdalnego
 * i wiersz udziału zarządzanego kolejki, której nie ma już w inwentarzu (pusty port).
 */
function groupable(printer: PrinterItem): boolean {
	return printer.portKind !== "virtual" && printer.portKind !== "redirected" && printer.port !== "";
}

/** Klucz grupy duplikatów: nazwa bez sufiksu kopii, bez rozróżniania wielkości liter. */
function duplicateKey(name: string): string {
	return name.replace(COPY_SUFFIX, "").trim().toLocaleLowerCase("pl");
}

/**
 * Kolejki prawdopodobnie zbędne: w grupie o tej samej nazwie bez „(Kopia N)”
 * (bez wirtualnych i przekierowanych) — kolejki offline, gdy obok jest
 * działająca; gdy wszystkie mają ten sam stan — kopie z sufiksem, o ile w
 * grupie została kolejka bez sufiksu (same kopie: nie wiadomo, która jest
 * właściwa, więc żadnej nie oznaczamy). To tylko podpowiedź, nie blokada.
 */
export function redundantPrinterNames(printers: PrinterItem[]): Set<string> {
	const groups = new Map<string, PrinterItem[]>();
	for (const printer of printers) {
		if (!groupable(printer)) continue;
		const key = duplicateKey(printer.name);
		groups.set(key, [...(groups.get(key) ?? []), printer]);
	}
	const redundant = new Set<string>();
	for (const group of groups.values()) {
		if (group.length < 2) continue;
		const offline = group.filter((p) => p.offline).length;
		const mixed = offline > 0 && offline < group.length;
		const copies = group.filter((p) => COPY_SUFFIX.test(p.name));
		for (const printer of group) {
			if (mixed ? printer.offline : copies.length < group.length && copies.includes(printer)) {
				redundant.add(printer.name);
			}
		}
	}
	return redundant;
}

/** Dymek znacznika „prawdopodobnie zbędna”, ze wskazaniem działającej kolejki tej drukarki. */
export function redundantPrinterHint(
	printer: PrinterItem,
	printers: PrinterItem[],
	redundant: ReadonlySet<string>,
): string {
	const key = duplicateKey(printer.name);
	const keep = printers.filter(
		(p) =>
			p.name !== printer.name &&
			groupable(p) &&
			!redundant.has(p.name) &&
			duplicateKey(p.name) === key,
	);
	const working = keep.find((p) => !p.offline) ?? keep[0];
	const base =
		"Windows założył tę drukarkę ponownie (zwykle po podłączeniu jej do innego gniazda USB albo ponownym wykryciu w sieci), więc ta kolejka jest prawdopodobnie pozostałością.";
	if (!working) return base;
	return working.offline
		? `${base} Ta sama drukarka jest też jako „${working.name}”.`
		: `${base} Działa „${working.name}” — tę można usunąć.`;
}

/** Microsoft Print to PDF rozpoznajemy po sterowniku; bez sterownika w inwentarzu — po nazwie. */
const PRINT_TO_PDF = /^microsoft print to pdf$/i;

/**
 * Czy panel w ogóle proponuje usunięcie kolejki. Nigdy: drukarka z pulpitu
 * zdalnego (odtwarza ją sesja RDP), Microsoft Print to PDF i wiersz udziału
 * zarządzanego drukarki, której nie ma już na komputerze (pusty port).
 */
export function printerRemovable(printer: PrinterItem): boolean {
	if (printer.portKind === "redirected" || printer.port === "") return false;
	return !PRINT_TO_PDF.test(printer.driver ?? printer.name);
}

/** Usunąć można tylko ręczne połączenie z rozpoznanym właścicielem (połączenie leży w jego profilu). */
export function connectionRemovable(
	connection: DeviceConnection,
): connection is DeviceConnection & { userName: string } {
	return !connection.managed && connection.userName != null;
}

/**
 * Powód blokady usuwania wynikający z komputera (pokazywany raz na karcie,
 * nie przy każdej drukarce); `null` = komputer przyjmie polecenie.
 */
export function removeDeviceBlocker(
	device: PrinterDevice,
	removeMinAgentVersion: string,
): string | null {
	if (!device.online) return "Usuwanie drukarek jest możliwe, gdy komputer jest online.";
	if (!device.removeSupported) {
		return `Usuwanie drukarek wymaga agenta ${removeMinAgentVersion} lub nowszego.`;
	}
	return null;
}

/** Stopka okien akcji: okno niczego nie wykonuje, tylko dopisuje pozycję do planu. */
export const PLAN_DIALOG_NOTE =
	"Zmiana trafi do planu — komputery zmienią się dopiero po „Wykonaj” w planie.";

/** Stopka tych samych okien w panelu helpdesku (`immediate`): bez planu, operacja startuje od razu. */
export const IMMEDIATE_DIALOG_NOTE = "Zmiana wykona się na komputerze od razu po kliknięciu.";

/**
 * Czy Windows założy usuniętą pozycję sam, czy trzeba będzie dodać ją ręcznie:
 * `portKind` kolejki albo `null` dla ręcznego połączenia sieciowego.
 */
export function removeReinstallNote(portKind: string | null): { text: string; manual: boolean } {
	if (portKind == null) {
		return {
			text: "Połączenie można później dodać ponownie ręcznie. Zapisane w Windows poświadczenie do komputera z drukarką zostaje.",
			manual: true,
		};
	}
	switch (portKind) {
		case "usb":
		case "dot4":
			return {
				text: "Jeśli ta drukarka jest potrzebna, Windows założy jej kolejkę ponownie po podłączeniu drukarki kablem USB.",
				manual: false,
			};
		case "wsd":
			return {
				text: "Jeśli ta drukarka jest potrzebna, Windows założy ją ponownie, gdy znowu wykryje ją w sieci (WSD).",
				manual: false,
			};
		case "network":
			return {
				text: "Drukarki sieciowej (TCP/IP) Windows nie założy sam — gdy okaże się potrzebna, trzeba ją dodać ręcznie.",
				manual: true,
			};
		default:
			return {
				text: "Tej kolejki Windows nie założy sam — gdy okaże się potrzebna, trzeba ją dodać ręcznie. Kolejki USB i WSD Windows zakłada ponownie po podłączeniu drukarki.",
				manual: true,
			};
	}
}

/**
 * Powód, dla którego kolejki (`printer`) albo połączenia (`printer = null`)
 * nie da się teraz usunąć; `null` = można. Te same blokady sprawdza backend
 * (409 `share_active`, `printer_in_use`, `operation_running`, `device_offline`,
 * `agent_outdated`) — panel mówi o nich, zanim operator kliknie.
 */
export function removeBlocker(
	device: PrinterDevice,
	printer: PrinterItem | null,
	locked: boolean,
	removeMinAgentVersion: string,
): string | null {
	if (printer?.managedShare) return "Najpierw cofnij udostępnienie.";
	if (printer && printer.usedBy.length > 0) {
		const names = [...new Set(printer.usedBy.map((u) => u.displayName))].join(", ");
		// Ręczne połączenie stanowiska nie ma „Odłącz”, tylko „Usuń połączenie” na jego karcie.
		const how = printer.usedBy.every((u) => u.managed)
			? "najpierw je odłącz"
			: "najpierw odłącz je albo usuń ich połączenia";
		return `Drukują na niej inne stanowiska (${names}) — ${how}.`;
	}
	if (locked) return "Trwa inna operacja na drukarkach.";
	return removeDeviceBlocker(device, removeMinAgentVersion);
}

/** `a < b` dla wersji `x.y.z` (sufiks po `-`/`+` pomijany, brakujące części = 0). */
export function versionBelow(a: string, b: string): boolean {
	const parts = (v: string) =>
		v
			.split(/[-+]/)[0]
			.split(".")
			.map((n) => Number.parseInt(n, 10) || 0);
	const [pa, pb] = [parts(a), parts(b)];
	for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
		const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
		if (diff !== 0) return diff < 0;
	}
	return false;
}

/**
 * Powód, dla którego „Dodaj drukarkę sieciową” jest wyłączone (jedno zdanie na
 * karcie); `null` = komputer przyjmie wykrywanie i dodanie. `addSupported` to
 * agent ≥ `addMinAgentVersion` i inwentarz z `network` — przy nowym agencie
 * brakuje więc danych o sieci.
 */
export function addDeviceBlocker(device: PrinterDevice, addMinAgentVersion: string): string | null {
	if (!device.online) return "Komputer offline";
	if (device.addSupported) return null;
	if (!device.serviceVersion || versionBelow(device.serviceVersion, addMinAgentVersion)) {
		return `Wymaga agenta ${addMinAgentVersion} lub nowszego`;
	}
	return "Brak danych o sieci komputera — odśwież";
}

/**
 * Powód, dla którego naprawa drukarek i zmiana domyślnej (przyciski na karcie,
 * „Napraw…” i „Ustaw jako domyślną” przy pozycjach) nie są dostępne na
 * komputerze online z obsługą modułu; `null` = dostępne. Raz na karcie, przy
 * jej przyciskach — wiersze nie mają wtedy tych akcji.
 */
export function fixAgentBlocker(device: PrinterDevice, fixMinAgentVersion: string): string | null {
	return device.fixSupported
		? null
		: `Naprawa drukarek i zmiana domyślnej wymagają agenta ${fixMinAgentVersion} lub nowszego`;
}

/** „Napraw…” przy kolejce: poza przekierowaną z pulpitu zdalnego (409 `printer_not_fixable`) i wierszem spoza inwentarza. */
export function printerFixable(printer: PrinterItem): boolean {
	return printer.portKind !== "redirected" && printer.port !== "";
}

/** Pozycja komputera, która może zostać jego drukarką domyślną. */
export type DefaultOption = {
	/** Nazwa kolejki albo ścieżka połączenia — `printerName` dla `set-default`. */
	value: string;
	label: string;
	detail: string;
	isDefault: boolean;
};

/**
 * Kolejki komputera (jak „Napraw…”: bez przekierowanych z pulpitu zdalnego i
 * wierszy spoza inwentarza) i jego połączenia sieciowe — kandydatki na domyślną.
 */
export function localDefaultOptions(device: PrinterDevice): DefaultOption[] {
	const queues = device.printers.filter(printerFixable).map((p) => ({
		value: p.name,
		label: p.name,
		detail: `${portKindLabel(p.portKind)}${p.offline ? " · wyłączona" : ""}`,
		isDefault: p.isDefault,
	}));
	const connections = device.connections.map((c) => ({
		value: c.path,
		label: c.printerName ?? c.path,
		detail: [c.hostDisplayName ? `sieciowa z ${c.hostDisplayName}` : "sieciowa", c.userName]
			.filter(Boolean)
			.join(" · "),
		isDefault: c.isDefault,
	}));
	return [...queues, ...connections];
}

export type ShareForDefault = {
	host: PrinterDevice;
	printer: PrinterItem;
	/** Dlaczego udostępnienie nie ruszy teraz; `null` = może. */
	reason: string | null;
};

/**
 * Drukarki kablowe innych komputerów apteki, których ten komputer jeszcze nie
 * ma (nie drukuje na nich ani nie ma do nich połączenia) — do domyślnej
 * prowadzi „Udostępnij” z zaznaczonym „ustaw jako domyślną”.
 */
export function sharePrintersForDefault(
	device: PrinterDevice,
	devices: PrinterDevice[],
): ShareForDefault[] {
	const same = (a: string | null, b: string) => a != null && a.toLowerCase() === b.toLowerCase();
	const result: ShareForDefault[] = [];
	for (const host of devices) {
		if (host.deviceUuid === device.deviceUuid) continue;
		for (const printer of host.printers) {
			if (!printer.cable) continue;
			const has =
				printer.usedBy.some((u) => u.deviceUuid === device.deviceUuid) ||
				device.connections.some(
					(c) => c.hostDeviceUuid === host.deviceUuid && same(c.printerName, printer.name),
				);
			if (has) continue;
			const reason = !host.online
				? "komputer z drukarką jest offline"
				: !host.supported
					? "komputer z drukarką ma za starego agenta"
					: null;
			result.push({ host, printer, reason });
		}
	}
	return result;
}

/** „Zainstaluj ponownie” tylko dla kolejki sieciowej TCP/IP z adresem — USB i WSD Windows odtwarza sam. */
export function printerReinstallable(printer: PrinterItem): boolean {
	return printer.portKind === "network" && printer.address != null;
}

/**
 * Powód, dla którego „Zainstaluj ponownie” jest zablokowane; `null` = można.
 * Te same blokady sprawdza backend (409 `share_active`, `printer_in_use`, `no_network`).
 */
export function reinstallBlocker(device: PrinterDevice, printer: PrinterItem): string | null {
	if (printer.managedShare) {
		return "Drukarka jest udostępniona przez Farmaster — najpierw cofnij udostępnienie.";
	}
	if (printer.shared) {
		return "Drukarka jest udostępniona w Windows — ponowna instalacja zdjęłaby udział. Wyłącz udostępnianie w Windows i spróbuj ponownie.";
	}
	if (printer.usedBy.length > 0) {
		const names = [...new Set(printer.usedBy.map((u) => u.displayName))].join(", ");
		return `Drukują na niej inne stanowiska (${names}) — najpierw je odłącz albo usuń ich połączenia.`;
	}
	// Agent z naprawą zna sieć; bez niej w inwentarzu backend odmówi (409 `no_network`).
	if (!device.addSupported)
		return "Brak danych o sieci komputera — kliknij „Odśwież” i spróbuj ponownie.";
	return null;
}

const sameDriver = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Sterowniki do ponownej instalacji: `availableDrivers` komputera, a gdy brakuje
 * na niej bieżącego sterownika kolejki — także on (backend przyjmuje oba).
 */
export function reinstallDriverOptions(device: PrinterDevice, printer: PrinterItem): string[] {
	const current = printer.driver;
	if (!current || device.availableDrivers.some((d) => sameDriver(d, current))) {
		return device.availableDrivers;
	}
	return [current, ...device.availableDrivers];
}

/** Bieżący sterownik kolejki w pisowni z listy; `null`, gdy kolejka go nie zgłasza. */
export function currentDriverOption(options: string[], printer: PrinterItem): string | null {
	const current = printer.driver;
	return current ? (options.find((d) => sameDriver(d, current)) ?? null) : null;
}

/** Ogólny sterownik Microsoft (PCL6/PS) — drukuje bez funkcji producenta. */
export function isGenericDriver(name: string): boolean {
	return /^microsoft (pcl6|ps) class driver$/i.test(name.trim());
}

/** „1 zadanie”, „3 zadania”, „5 zadań”. */
export function jobsCountLabel(count: number): string {
	return `${count} ${pluralPl(count, "zadanie", "zadania", "zadań")}`;
}

/** Wiek zadania po „od”: „niecałej minuty”, „40 min”, „3 godz.”, „2 dni”. */
function jobAge(fromIso: string, now: number): string | null {
	const time = new Date(fromIso).getTime();
	if (Number.isNaN(time)) return null;
	const minutes = Math.max(0, Math.floor((now - time) / 60_000));
	if (minutes < 1) return "niecałej minuty";
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 48) return `${hours} godz.`;
	return `${Math.floor(hours / 24)} dni`;
}

/** „3 zadania w kolejce, najstarsze od 40 min.”; pusta kolejka i brak danych (starszy agent) osobno. */
export function jobsSummary(jobs: PrinterJobs | null, now: number = Date.now()): string {
	if (!jobs) return "Liczba zadań w kolejce nieznana.";
	if (jobs.count === 0) return "Kolejka jest pusta.";
	const age = jobs.oldestAt ? jobAge(jobs.oldestAt, now) : null;
	const since = age ? `, ${jobs.count === 1 ? "czeka" : "najstarsze"} od ${age}` : "";
	const text = `${jobsCountLabel(jobs.count)} w kolejce${since}`;
	// „3 godz.” już kończy się kropką.
	return text.endsWith(".") ? text : `${text}.`;
}

/**
 * Zadania, które zginą przy resecie bufora: suma po kolejkach komputera (bez
 * wiersza udziału kolejki spoza inwentarza); `null` = nieznana (brak `jobs`).
 */
export function spoolerResetJobs(printers: PrinterItem[]): number | null {
	let total = 0;
	for (const printer of printers) {
		if (printer.port === "") continue;
		if (!printer.jobs) return null;
		total += printer.jobs.count;
	}
	return total;
}

/** Treść potwierdzenia „Zresetuj bufor wydruku”: co się stanie, ile wydruków zginie, kto straci drukarki. */
export function spoolerResetDescription(device: PrinterDevice): string {
	const jobs = spoolerResetJobs(device.printers);
	const lost =
		jobs == null
			? "Zginą wszystkie oczekujące wydruki tego komputera (nieznana liczba)."
			: jobs === 0
				? "W kolejkach tego komputera nie ma teraz zadań do wydruku."
				: `Zginą wszystkie oczekujące wydruki tego komputera (${jobsCountLabel(jobs)}).`;
	const hosting = device.printers.some(
		(p) => p.port !== "" && (p.managedShare != null || p.shared),
	);
	return [
		`Usługa Bufor wydruku na komputerze ${device.displayName} zostanie zatrzymana, zawieszone pliki zadań usunięte, a usługa uruchomiona ponownie.`,
		lost,
		hosting
			? "Ten komputer udostępnia drukarki — stanowiska, które na nich drukują, stracą je na kilka sekund."
			: null,
	]
		.filter(Boolean)
		.join(" ");
}

/**
 * Powód z odpowiedzi listy drukarek sieciowych, dla którego okno nie doda
 * drukarki; `null` = może. Przy braku `network` zawsze mówi o sieci.
 */
export function networkAddUnavailableReason(data: NetworkPrintersResponse): string | null {
	if (!data.online) {
		return "Komputer jest offline — wykrywanie i dodawanie drukarek będą możliwe, gdy wróci do sieci.";
	}
	if (data.addSupported && data.network) return null;
	if (data.unsupportedReason === "agent_outdated") {
		return `Dodawanie drukarek wymaga agenta ${data.addMinAgentVersion} lub nowszego.`;
	}
	if (data.unsupportedReason === "no_inventory") {
		return "Agent nie przesłał jeszcze listy drukarek tego komputera — kliknij „Odśwież” za chwilę.";
	}
	return "Komputer nie zgłosił sieci z bramą domyślną — kliknij „Odśwież”, a gdy to nie pomoże, sprawdź jego połączenie sieciowe.";
}

const IPV4_OCTET = "(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)";
const IPV4 = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}$`);

/** Kształt adresu IPv4 (`192.168.1.50`); czy adres jest w sieci komputera, sprawdza backend. */
export function isIpv4(value: string): boolean {
	return IPV4.test(value);
}

/** Najdłuższa nazwa kolejki, jaką przyjmuje backend i agent (jak przy udostępnianiu). */
export const PRINTER_NAME_MAX = 220;

/** Znak sterujący C0, DEL albo C1 (`char::is_control` po stronie backendu). */
function isControlChar(ch: string): boolean {
	const code = ch.charCodeAt(0);
	return code < 0x20 || (code >= 0x7f && code <= 0x9f);
}

/**
 * Błąd nazwy nowej kolejki albo `null`: 1–220 znaków, bez `\`, `,` i znaków
 * sterujących, wolna na komputerze (Windows nie rozróżnia wielkości liter —
 * zajętą nazwę backend odrzuca 409 `printer_exists`).
 */
export function printerNameError(name: string, existing: readonly string[]): string | null {
	const trimmed = name.trim();
	if (trimmed === "") return "Podaj nazwę drukarki.";
	if ([...trimmed].length > PRINTER_NAME_MAX) {
		return `Nazwa może mieć najwyżej ${PRINTER_NAME_MAX} znaków.`;
	}
	if (/[\\,]/.test(trimmed)) return "Nazwa nie może zawierać znaków „\\” ani „,”.";
	if ([...trimmed].some(isControlChar)) return "Nazwa nie może zawierać znaków sterujących.";
	const lower = trimmed.toLocaleLowerCase("pl");
	if (existing.some((n) => n.toLocaleLowerCase("pl") === lower)) {
		return "Na tym komputerze jest już drukarka o tej nazwie.";
	}
	return null;
}

/** Błąd wykrywania (`discovery.error`) po polsku; nieznany kod zostaje bez zmian. */
const DISCOVERY_ERRORS: Record<string, string> = {
	no_network: "komputer nie ma karty sieciowej z bramą domyślną",
	socket_failed: "Windows nie pozwolił wysłać zapytania w sieci",
	timeout: "komputer nie odpowiedział na czas",
	command_timeout: "komputer nie odpowiedział na czas",
	busy: "na komputerze trwa inna operacja na drukarkach — spróbuj za chwilę",
	device_offline: "komputer rozłączył się z Farmasterem",
	host_outside_network: "adres nie leży w sieci komputera",
	network_changed:
		"komputer jest już w innej sieci niż w ostatnich danych — dane odświeżą się same za chwilę, potem kliknij „Wykryj ponownie”",
	invalid_request: "agent odrzucił zlecenie jako niepoprawne",
	agent_error: "błąd agenta Farmastera",
};

export function discoveryErrorLabel(error: string | null): string {
	if (!error) return "nieznany błąd";
	return DISCOVERY_ERRORS[error] ?? error;
}

/** Miejscownik „komputer” po liczebniku: na 1 komputerze, na 2 (5, 22…) komputerach. */
function computersLocative(count: number): string {
	return count === 1 ? "komputerze" : "komputerach";
}

/** Adres drukarki: `192.168.1.20:9100`, a dla LPR z nazwą kolejki. */
export function networkPrinterAddress(
	printer: Pick<NetworkPrinterCandidate, "host" | "port" | "protocol" | "queue">,
): string {
	const base = `${printer.host}:${printer.port}`;
	if (printer.protocol !== "lpr") return base;
	return printer.queue ? `${base} · LPR, kolejka ${printer.queue}` : `${base} · LPR`;
}

/**
 * Skąd panel wie o drukarce: „na 3 komputerach w tej sieci: a, b, c”, „wykryta
 * w sieci (WSD, mDNS)”, „sprawdzony adres” — połączone kropką.
 */
export function networkPrinterSource(printer: NetworkPrinterCandidate): string {
	const { deviceCount, devices, via } = printer.sources;
	const parts: string[] = [];
	if (deviceCount > 0) {
		const rest = deviceCount - devices.length;
		const names =
			devices.length > 0
				? `: ${devices.join(", ")}${rest > 0 ? ` i ${rest} ${pluralPl(rest, "inny", "inne", "innych")}` : ""}`
				: "";
		parts.push(`na ${deviceCount} ${computersLocative(deviceCount)} w tej sieci${names}`);
	}
	const found = via.filter((v) => v !== "manual").map((v) => (v === "wsd" ? "WSD" : "mDNS"));
	if (found.length > 0) parts.push(`wykryta w sieci (${found.join(", ")})`);
	if (via.includes("manual")) parts.push("sprawdzony adres");
	return parts.join(" · ");
}

/** Oznaczenia sterownika na liście: zainstalowany, ogólny, używany na N komputerach. */
export function driverNotes(driver: NetworkPrinterDriver): string[] {
	const notes: string[] = [];
	if (driver.installed) notes.push("zainstalowany na komputerze");
	if (driver.generic) notes.push("ogólny");
	if (driver.usedBy > 0) {
		notes.push(`używany na ${driver.usedBy} ${computersLocative(driver.usedBy)}`);
	}
	return notes;
}

export const GENERIC_DRIVER_NOTE =
	"Sterownik ogólny drukuje bez funkcji producenta (np. dupleksu czy wyboru podajnika), a drukarka GDI go nie zrozumie. Wybierz go, gdy nie ma sterownika producenta.";

/** Powód, dla którego urządzenie nie może zostać stanowiskiem B; `null` = może. */
export function clientUnavailableReason(
	device: PrinterDevice,
	minAgentVersion: string,
	alreadyUsing: boolean,
): string | null {
	if (alreadyUsing) return "już korzysta z tej drukarki";
	if (!device.online) return "offline";
	if (!device.supported) {
		return device.unsupportedReason === "agent_outdated"
			? `wymaga agenta ${minAgentVersion} lub nowszego`
			: "brak danych o drukarkach z tego urządzenia";
	}
	return null;
}

/** Data i godzina w strefie przeglądarki, sortowalna leksykograficznie (`2026-10-15 08:05`). */
export function formatDateTime(value: string | null): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
